class ComponentHeader extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();
    this.initHeaderHeight();
    this.initHeaderSentinel();
    this.initCollapseWatch();
  }

  disconnectedCallback() {

    super.disconnectedCallback();
    this.destroyCollapseWatch();
    this.destroyHeaderSentinel();
    this.destroyHeaderHeight();
  }

  initHeaderHeight() {
    this.updateHeaderHeightVar();

    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
      this.headerResizeObserver = null;
    }

    this.headerResizeObserver = new ResizeObserver(() => {
      if (this.isCollapseAnimating) return;

      this.updateHeaderHeightVar();
      this.cacheCollapsibleHeight();

      if (this.headerSentinelObserver) {
        this.initHeaderSentinel();
      }
    });

    this.headerResizeObserver.observe(this);
  }

  destroyHeaderHeight() {
    if (!this.headerResizeObserver) return;
    this.headerResizeObserver.disconnect();
    this.headerResizeObserver = null;
  }

  updateHeaderHeightVar() {
    const height = this.getBoundingClientRect().height || 0;
    document.documentElement.style.setProperty('--fixed-header-height', `${height}px`);
  }

  cacheCollapsibleHeight() {
    if (!this.collapsible || this.classList.contains('is-scrolled')) return;

    const height = this.collapsible.getBoundingClientRect().height || 0;
    if (height > 0) {
      this.collapsibleHeight = height;
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    }

    const headerHeight = this.getBoundingClientRect().height || 0;
    if (headerHeight > 0) this.expandedHeight = headerHeight;
  }

  setCollapseTargetHeight(collapsing) {
    if (!this.collapsible || !this.expandedHeight) return;

    const target = collapsing ? this.expandedHeight - (this.collapsibleHeight || 0) : this.expandedHeight;
    document.documentElement.style.setProperty('--fixed-header-height', `${target}px`);
  }

  initCollapseWatch() {
    this.collapsible = this.querySelector('.collapse');
    if (!this.collapsible) {
      document.documentElement.style.setProperty('--announcement-height', '0px');
      return;
    }

    this.cacheCollapsibleHeight();

    this.onCollapseStart = (event) => {
      if (event.target !== this.collapsible || event.propertyName !== 'grid-template-rows') return;

      this.isCollapseAnimating = true;
    };

    this.onCollapseEnd = (event) => {
      if (event.target !== this.collapsible || event.propertyName !== 'grid-template-rows') return;

      this.isCollapseAnimating = false;
      this.updateHeaderHeightVar();
      this.cacheCollapsibleHeight();
      this.initHeaderSentinel();
    };

    this.collapsible.addEventListener('transitionstart', this.onCollapseStart);
    this.collapsible.addEventListener('transitionend', this.onCollapseEnd);
    this.collapsible.addEventListener('transitioncancel', this.onCollapseEnd);
  }

  destroyCollapseWatch() {
    if (!this.collapsible) return;

    this.collapsible.removeEventListener('transitionstart', this.onCollapseStart);
    this.collapsible.removeEventListener('transitionend', this.onCollapseEnd);
    this.collapsible.removeEventListener('transitioncancel', this.onCollapseEnd);
    this.collapsible = null;
    this.isCollapseAnimating = false;
  }

  initHeaderSentinel() {
    const sentinel = document.querySelector('[data-header-sentinel]');
    if (!sentinel) return;

    if (this.headerSentinelObserver) {
      this.headerSentinelObserver.disconnect();
      this.headerSentinelObserver = null;
    }

    const headerHeight = Math.ceil(this.getBoundingClientRect().height || 0);

    this.headerSentinelObserver = new IntersectionObserver(
      (entries) => this.onHeaderSentinelIntersect(entries),
      {
        root: null,
        threshold: 0,
        rootMargin: `${headerHeight + 60}px 0px 0px 0px`
      }
    );

    this.headerSentinelObserver.observe(sentinel);
  }

  destroyHeaderSentinel() {
    if (!this.headerSentinelObserver) return;
    this.headerSentinelObserver.disconnect();
    this.headerSentinelObserver = null;
  }

  onHeaderSentinelIntersect(entries) {
    const entry = entries && entries[0];
    if (!entry) return;

    if (entry.isIntersecting) {
      this.classList.remove('is-scrolled');
      this.setCollapseTargetHeight(false);
      return;
    }

    this.classList.add('is-scrolled');
    this.setCollapseTargetHeight(true);
  }
}

customElements.define('component-header', ComponentHeader);